import { Controller } from "@hotwired/stimulus"

// Live preview controller for campaign creation/edition.
// Recomputes the destination URL with UTM params and asks the server
// for an SVG QR code (debounced to avoid hammering it).
export default class extends Controller {
    static targets = [
        "name",
        "source",
        "medium",
        "campaign",
        "external",
        "funnel",
        "target",            // new: polymorphic offer picker
        "urlOutput",         // destination URL (UTM-tracked)
        "shortLinkOutput",   // canonical beb.tn/SHORTCODE link (server-rendered when persisted)
        "qrOutput",
        "copyInput",
        "downloadLink"
    ]

    static values = {
        baseUrl: String,
        previewEndpoint: String,
        funnelUrls: Object
    }

    connect() {
        this.debounceTimer = null
        this.update()
    }

    disconnect() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        if (this.lastObjectUrl) URL.revokeObjectURL(this.lastObjectUrl)
    }

    update() {
        // Auto-fill utm_source from name if empty
        this.maybeAutofillSource()

        const destination = this.buildUrl()
        if (this.hasUrlOutputTarget) this.urlOutputTarget.textContent = destination
        if (this.hasCopyInputTarget) this.copyInputTarget.value = destination

        // QR encodes the short link when available (stable, points back to beb
        // so the click is tracked). Falls back to the destination otherwise.
        const short  = this.hasShortLinkOutputTarget ? this.shortLinkOutputTarget.textContent.trim() : ""
        const qrText = short && !short.includes("···") ? short : destination

        if (this.debounceTimer) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => this.refreshQr(qrText), 300)
    }

    maybeAutofillSource() {
        if (!this.hasSourceTarget || !this.hasNameTarget) return
        const source = this.sourceTarget
        const name = this.nameTarget
        if (source.dataset.userEdited === "1") return
        if (source.value && source.value.length > 0 && !source.dataset.autofilled) return

        const slug = (name.value || "")
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .substring(0, 50)
        source.value = slug || "campaign"
        source.dataset.autofilled = "1"
    }

    markSourceEdited(event) {
        if (event && event.target) {
            event.target.dataset.userEdited = "1"
        }
    }

    buildUrl() {
        let base = this.baseUrlValue || ""

        if (this.hasExternalTarget && this.externalTarget.value && this.externalTarget.value.trim().length > 0) {
            base = this.externalTarget.value.trim()
        } else if (this.hasFunnelTarget) {
            const fid = this.funnelTarget.value
            if (fid && this.hasFunnelUrlsValue && this.funnelUrlsValue[fid]) {
                base = this.funnelUrlsValue[fid]
            }
        }

        if (!base) base = this.baseUrlValue || "https://example.com"

        const params = new URLSearchParams()
        const source = this.hasSourceTarget ? this.sourceTarget.value.trim() : ""
        const medium = this.hasMediumTarget ? this.mediumTarget.value.trim() : ""
        const campaign = this.hasCampaignTarget ? this.campaignTarget.value.trim() : ""

        if (source) params.append("utm_source", source)
        if (medium) params.append("utm_medium", medium)
        if (campaign) params.append("utm_campaign", campaign)

        const qs = params.toString()
        if (!qs) return base
        const sep = base.includes("?") ? "&" : "?"
        return `${base}${sep}${qs}`
    }

    async refreshQr(url) {
        if (!this.hasQrOutputTarget) return
        if (!this.hasPreviewEndpointValue) return
        if (!url || url.length > 500) return

        try {
            const endpoint = `${this.previewEndpointValue}?url=${encodeURIComponent(url)}`
            const resp = await fetch(endpoint, {
                headers: { "Accept": "image/svg+xml" },
                credentials: "same-origin"
            })
            if (!resp.ok) return
            const svg = await resp.text()
            // Server-rendered, trusted internal endpoint — render inline SVG.
            this.qrOutputTarget.innerHTML = svg // eslint-disable-line no-unsanitized/property

            if (this.hasDownloadLinkTarget) {
                const blob = new Blob([svg], { type: "image/svg+xml" })
                const objUrl = URL.createObjectURL(blob)
                if (this.lastObjectUrl) URL.revokeObjectURL(this.lastObjectUrl)
                this.lastObjectUrl = objUrl
                this.downloadLinkTarget.href = objUrl
                this.downloadLinkTarget.setAttribute("download", "campaign-qr.svg")
            }
        } catch (err) {
            console.warn("[campaign-preview] QR fetch failed", err)
        }
    }

    copyUrl(event) {
        if (event) event.preventDefault()
        // Prefer the short link (visible on persisted campaigns); fall back
        // to the live destination URL when the campaign hasn't been saved yet.
        const short = this.hasShortLinkOutputTarget ? this.shortLinkOutputTarget.textContent.trim() : ""
        const text  = short && !short.includes("···") ?
                      short :
                      (this.hasCopyInputTarget ? this.copyInputTarget.value : "")
        if (!text) return
        navigator.clipboard.writeText(text).then(() => {
            const btn = event && event.currentTarget
            if (!btn) return
            const orig = btn.dataset.origLabel || btn.textContent
            btn.dataset.origLabel = orig
            btn.textContent = "Copié !"
            setTimeout(() => { btn.textContent = orig }, 1500)
        })
    }
}
